import { useCallback, useLayoutEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import { useAuth } from "context/AuthContext";
import { useAppDispatch } from "store/hooks";
import { setIsSpinnerVisible } from "store/modules/AppConfig/index.store";
import { fetchDealerData, fetchProviders } from "../services/dealerDataService";
import {
  extractLocalPaymentFromProviders,
  processAllApiResults,
} from "../utils/processApiResults";
import { ERROR_MESSAGES } from "../constants";
import { getErrorMessage } from "../utils/errorHandler";
import type { DealerByIdState } from "../types";

export function useDealerData() {
  const { dealerId } = useAuth();
  const dispatch = useAppDispatch();

  const [state, setState] = useState<DealerByIdState>({
    dealerData: null,
    providers: [],
    availableProviders: [],
    merchantInfo: null,
    localPaymentData: null,
    paymentMethods: null,
  });

  const loadDealerData = useCallback(
    async (id: string) => {
      if (!id) return;
      dispatch(setIsSpinnerVisible(true));
      try {
        const results = await fetchDealerData(id);

        const processedData = processAllApiResults(results);

        setState((prev) => ({ ...prev, ...processedData }));
      } catch (error) {
        const errorMessage = getErrorMessage(
          error,
          ERROR_MESSAGES.DEALER_LOAD_FAILED,
        );
        toast(errorMessage, { type: "error" });
      } finally {
        dispatch(setIsSpinnerVisible(false));
      }
    },
    [dispatch],
  );

  const refreshProviders = useCallback(async (merchantId: string) => {
    try {
      const providers = await fetchProviders(merchantId);
      const localPaymentData = extractLocalPaymentFromProviders(providers);

      setState((prev) => ({
        ...prev,
        providers,
        localPaymentData,
      }));

      return providers;
    } catch (error) {
      const errorMessage = getErrorMessage(
        error,
        ERROR_MESSAGES.DEALER_LOAD_FAILED,
      );
      toast(errorMessage, { type: "error" });
      return null;
    }
  }, []);

  const updateMcc = useCallback((categoryCode: string) => {
    setState((prev) => ({
      ...prev,
      merchantInfo: prev.merchantInfo
        ? { ...prev.merchantInfo, categoryCode }
        : ({ categoryCode } as DealerByIdState["merchantInfo"]),
    }));
  }, []);

  const removeProvider = useCallback(async (providerId: string) => {
    setState((prev) => {
      const providerToDelete = prev.providers.find(
        (provider) => provider.id === providerId,
      );

      if (!providerToDelete) {
        return {
          ...prev,
          providers: prev.providers.filter(
            (provider) => provider.id !== providerId,
          ),
        };
      }

      const deletedPriority = providerToDelete.priority;
      const remainingProviders = prev.providers.filter(
        (provider) => provider.id !== providerId,
      );

      const updatedProviders = remainingProviders.map((provider) => {
        if (provider.priority > deletedPriority) {
          return {
            ...provider,
            priority: provider.priority - 1,
          };
        }
        return provider;
      });

      return {
        ...prev,
        providers: updatedProviders,
      };
    });
  }, []);

  const addProvider = useCallback(
    (provider: DealerByIdState["providers"][number]) => {
      setState((prev) => ({
        ...prev,
        providers: [...prev.providers, provider],
      }));
    },
    [],
  );

  const updateProviderState = useCallback(
    (providerId: string, newState: string) => {
      setState((prev) => ({
        ...prev,
        providers: prev.providers.map((provider) =>
          provider.id === providerId
            ? { ...provider, state: newState }
            : provider,
        ),
      }));
    },
    [],
  );

  const updateProvider = useCallback(
    (updatedProvider: DealerByIdState["providers"][number]) => {
      setState((prev) => ({
        ...prev,
        providers: prev.providers.map((provider) =>
          provider.id === updatedProvider.id ? updatedProvider : provider,
        ),
      }));
    },
    [],
  );

  const hasMounted = useRef(false);
  useLayoutEffect(() => {
    if (hasMounted.current) return;
    hasMounted.current = true;
    if (dealerId) {
      loadDealerData(dealerId);
    }
  }, [dealerId, loadDealerData]);

  return {
    ...state,
    dealerId,
    loadDealerData,
    refreshProviders,
    updateMcc,
    removeProvider,
    addProvider,
    updateProviderState,
    updateProvider,
  };
}
